/**
 * The reasons a passenger may give for cancelling, as the firm keeps them (#464).
 *
 * The passenger app offers the active ones as cards after *Отказ*, in this order; the one
 * chosen is recorded on the order as `rider.<code>` and named in its history on the desk. A
 * reason is switched off rather than deleted, because past orders name it by code. The five
 * starters say themselves in the reader's language until the firm types words of its own.
 *
 * Each control saves when pressed, as on the order labels page beside it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { forgetCancellationReasons } from '../../orders/cancellationReasons';
import { cancellationReasonName, STARTER_CANCELLATION_REASONS } from '../../orders/history';
import {
  Banner,
  Button,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  PageHeader,
  Section,
  Sub,
  Switch,
  Table,
  Td,
  Th,
  Tr,
} from '../../ui';
import {
  cancellationReasonsApi,
  type CancellationReason,
  type CancellationReasonRequest,
} from '../api';
import { describe } from '../errors';
import { moved } from '../ordering';

function bodyOf(
  reason: CancellationReason,
  changes: Partial<CancellationReasonRequest> = {},
): CancellationReasonRequest {
  return {
    // A starter's own words sent back unchanged leave it unrenamed.
    displayName: reason.displayName,
    sortOrder: reason.sortOrder,
    isActive: reason.isActive,
    ...changes,
  };
}

export function CancellationReasonsPage() {
  const { t } = useTranslation();

  const [reasons, setReasons] = useState<CancellationReason[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<CancellationReason | 'new' | null>(null);
  const [name, setName] = useState('');
  const [dialogError, setDialogError] = useState<string | null>(null);

  const firstBox = useRef<HTMLInputElement>(null);

  const nameOf = useCallback(
    (reason: CancellationReason) =>
      cancellationReasonName(t, reason.code, reason.displayName, reason.namedByFirm),
    [t],
  );

  const load = useCallback(async () => {
    try {
      setReasons(await cancellationReasonsApi.list());
    } catch (failure) {
      setError(describe(failure, t, 'settings.couldNotLoad'));
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- fetching, not cascading
    void load();
  }, [load]);

  const dialogOpen = editing !== null;

  useEffect(() => {
    if (!dialogOpen) return;

    // After showModal has done its own focusing, which it does on the same tick.
    const frame = requestAnimationFrame(() => firstBox.current?.focus());

    return () => cancelAnimationFrame(frame);
  }, [dialogOpen]);

  function settle(reason: CancellationReason) {
    setReasons((current) => current?.map((row) => (row.id === reason.id ? reason : row)) ?? current);
  }

  async function act(action: () => Promise<void>, inDialog = false) {
    setBusy(true);
    setError(null);
    setDialogError(null);

    try {
      await action();

      // So this console's order history says the new name without a reload.
      forgetCancellationReasons();
    } catch (failure) {
      (inDialog ? setDialogError : setError)(describe(failure, t, 'settings.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  function open(reason: CancellationReason | 'new') {
    setDialogError(null);
    setName(reason === 'new' ? '' : nameOf(reason));
    setEditing(reason);
  }

  function save() {
    const typed = name.trim();

    // Enter in the box reaches here as well as the button, which is disabled while busy.
    if (busy || editing === null || typed === '') return;

    void act(async () => {
      if (editing === 'new') {
        await cancellationReasonsApi.add({ displayName: typed, isActive: true });
        await load();
      } else {
        // The translated name left as it was is not a rename.
        const displayName = typed === nameOf(editing) ? editing.displayName : typed;

        settle(await cancellationReasonsApi.save(editing.id, bodyOf(editing, { displayName })));
      }

      // Closed only if it is still the one this saved: it may have been closed, and another
      // opened, while the request ran.
      setEditing((open) => (open === editing ? null : open));
    }, true);
  }

  function move(index: number, step: -1 | 1) {
    if (reasons === null) return;

    const changes = moved(reasons, index, step);

    void act(async () => {
      try {
        for (const change of changes) {
          const reason = reasons.find((row) => row.id === change.id);

          if (reason !== undefined) {
            await cancellationReasonsApi.save(reason.id, bodyOf(reason, { sortOrder: change.sortOrder }));
          }
        }
      } finally {
        // Also after a refusal halfway through, so the screen shows what the server has.
        await load();
      }
    });
  }

  const current = editing !== null && editing !== 'new' ? editing : null;

  return (
    <>
      <PageHeader
        title={t('nav.settings.cancellations')}
        description={t('nav.settingsAbout.cancellations')}
      />

      <Section
        title={t('cancellations.title')}
        description={t('cancellations.help')}
        actions={
          <Button size="sm" disabled={busy} onClick={() => open('new')}>
            <Icon name="plus" small />
            {t('cancellations.add')}
          </Button>
        }
      >
        {error !== null && (
          <div className="px-5 pt-3">
            <Banner tone="danger">{error}</Banner>
          </div>
        )}

        {reasons === null ? (
          error === null && <EmptyState>{t('app.loading')}</EmptyState>
        ) : reasons.length === 0 ? (
          <EmptyState>{t('cancellations.none')}</EmptyState>
        ) : (
          <Table tight>
            <thead>
              <Tr>
                <Th>{t('cancellations.name')}</Th>
                <Th>{t('cancellations.active')}</Th>
                <Th>{t('cancellations.order')}</Th>
                <Th />
              </Tr>
            </thead>
            <tbody>
              {reasons.map((reason, index) => (
                <Tr key={reason.id}>
                  <Td wrap>
                    <span className={reason.isActive ? '' : 'text-ink-3'}>{nameOf(reason)}</span>
                    {!reason.namedByFirm && STARTER_CANCELLATION_REASONS.includes(reason.code) && (
                      <Sub block>{t('cancellations.translated')}</Sub>
                    )}
                  </Td>
                  <Td>
                    <Switch
                      checked={reason.isActive}
                      disabled={busy}
                      label={t('cancellations.activeOf', { name: nameOf(reason) })}
                      onChange={(next) =>
                        void act(async () =>
                          settle(
                            await cancellationReasonsApi.save(reason.id, bodyOf(reason, { isActive: next })),
                          ),
                        )
                      }
                    />
                  </Td>
                  <Td>
                    <span className="flex gap-1">
                      <Button
                        size="sm"
                        icon
                        disabled={busy || index === 0}
                        aria-label={t('settings.moveUp')}
                        onClick={() => move(index, -1)}
                      >
                        <Icon name="arrow-up" small />
                      </Button>
                      <Button
                        size="sm"
                        icon
                        disabled={busy || index === reasons.length - 1}
                        aria-label={t('settings.moveDown')}
                        onClick={() => move(index, 1)}
                      >
                        <Icon name="arrow-down" small />
                      </Button>
                    </span>
                  </Td>
                  <Td>
                    <span className="flex justify-end">
                      <Button size="sm" disabled={busy} onClick={() => open(reason)}>
                        {t('cancellations.rename')}
                      </Button>
                    </span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      <Dialog
        open={editing !== null}
        title={current === null ? t('cancellations.add') : t('cancellations.renameTitle', { name: nameOf(current) })}
        onClose={() => setEditing(null)}
        actions={
          <>
            <Button variant="quiet" onClick={() => setEditing(null)}>
              {t('settings.cancel')}
            </Button>
            <Button variant="primary" disabled={busy || name.trim() === ''} onClick={save}>
              {current === null ? t('cancellations.add') : t('settings.save')}
            </Button>
          </>
        }
      >
        <div
          className="flex flex-col gap-3.5"
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || !(e.target instanceof HTMLInputElement)) return;
            e.preventDefault();
            save();
          }}
        >
          {dialogError !== null && <Banner tone="danger">{dialogError}</Banner>}

          <Field
            id="displayName"
            label={t('cancellations.name')}
            help={
              current !== null && !current.namedByFirm && STARTER_CANCELLATION_REASONS.includes(current.code)
                ? t('cancellations.nameStarterHelp')
                : t('cancellations.nameHelp')
            }
          >
            <Input ref={firstBox} value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
          </Field>
        </div>
      </Dialog>
    </>
  );
}
